import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { OnyxTab } from '@onyx/angular';
import {
  onyxMinNumberValidator,
  OnyxSectionComponent,
  OnyxTabsComponent,
  OnyxFormGroupComponent,
  OnyxSuggestionsComponent,
  OnyxTextFieldComponent,
  OnyxTooltipDirective,
} from '@onyx/angular';
import type { FleetAdditionalParametersFormGroup } from '../../../../common/components/forms/parameters-form/parameters-form.component';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ValidationHelper } from '../../../../common/helpers/validation.helper';
import type { ValidationGroup } from '../../../../common/interfaces/validation/validation-group';
import { DictionariesService } from '../../../../common/services/dictionaries.service';
import type { FleetCategory } from '../../common/enums/fleet-category';
import type { SemiTrailerSize } from '../../common/enums/semi-trailer-size';
import { UnitSuggestionsPipe } from '../../../../common/pipes/unit-suggestions.pipe';
import { UnitWarningPipe } from '../../../../common/pipes/unit-warning.pipe';

export type FleetVehicleMeasurementsFormGroup = ReturnType<
  typeof FleetVehicleMeasurementsFormComponent.buildForm
>;

@Component({
  selector: 'app-fleet-vehicle-measurements-form',
  imports: [
    UnitWarningPipe,
    UnitSuggestionsPipe,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxTextFieldComponent,
    OnyxTabsComponent,
    OnyxTooltipDirective,
    TranslatePipe,
  ],
  templateUrl: './fleet-vehicle-measurements-form.component.html',
  styleUrl: './fleet-vehicle-measurements-form.component.scss',
})
export class FleetVehicleMeasurementsFormComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly dictionariesService = inject(DictionariesService);
  private readonly injector = inject(Injector);

  public readonly form = input.required<FleetVehicleMeasurementsFormGroup>();
  public readonly validation = input.required<ValidationGroup>();
  public readonly parametersForm =
    input.required<FleetAdditionalParametersFormGroup>();
  public readonly parametersValidation = input.required<ValidationGroup>();
  public readonly category = input.required<FleetCategory>();
  public readonly hasAssignedFleet = input<boolean>(false);
  public readonly assignedSemiTrailerSize = input<SemiTrailerSize | null>(null);

  protected readonly I18N = 'fleet.fleetForm.vehicleMeasurements';
  protected readonly LOW_DECK_OPTIONS: OnyxTab<boolean>[] = [
    {
      name: 'labels.standardDeck',
      value: false,
    },
    {
      name: 'labels.lowDeck',
      value: true,
    },
  ];

  protected readonly DictionaryCode = DictionaryCode;

  protected axleCounts = signal<OnyxTab<number>[] | null>(null);

  protected showLowDeck = computed(() => {
    const field = this.parametersValidation()?.fields['isLowDeck'];
    return !!field && field.type !== 'forbidden';
  });

  public ngOnInit(): void {
    effect(
      () => {
        const fields = this.validation().fields;

        setTimeout(() => {
          ValidationHelper.updateListControl(
            this.form().controls.axleCount,
            fields['axleCount'],
            this.translateService,
            this.dictionariesService,
            DictionaryCode.VEHICLE_AXLE_COUNT,
            this.axleCounts,
            false,
          );

          const parametersFields = this.parametersValidation().fields;
          const parametersControls = this.parametersForm().controls;

          if (parametersControls.isLowDeck) {
            ValidationHelper.updateControl(
              parametersControls.isLowDeck,
              parametersFields?.['isLowDeck'] ??
                ({ type: 'forbidden' } as const),
            );
            ValidationHelper.toggleControls(parametersControls.isLowDeck, {
              enabled: !this.hasAssignedFleet(),
              reset: false,
            });
          }
        });
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      curbWeight: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(1),
      ]),
      axleCount: fb.control<number | null>(null),
      length: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      width: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
      height: fb.control<number | null>(null, [
        Validators.required,
        onyxMinNumberValidator(Number.EPSILON, 2),
      ]),
    });
  }
}
